import { useState, useRef, useCallback } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Plus, Trash2, GripVertical, Tag, Wand2, Loader2 } from 'lucide-react';
import { FormulaInput } from '../FormulaInput';
import { NotionPicker } from '../NotionPicker';
import { suggestNotion } from '../../lib/api';

const LETTERS = ['A', 'B', 'C', 'D', 'E', 'F'];

export function QuestionCard({ q, qIdx, sortableId, onUpdateQuestion, onUpdateOption, onAddOption, onRemoveOption, onRemoveQuestion, onToggleCorrect, onToggleType, canRemove, notionTags = [], tagsTree = [], sciMode = false }) {
  const isMulti = q.type === 'multiple';
  const correctSet = isMulti ? new Set(q.bonne_reponse.split(',').filter(Boolean)) : new Set([q.bonne_reponse]);
  const suggestTimer = useRef(null);
  const lastSuggested = useRef('');
  const [suggesting, setSuggesting] = useState(false);

  const doSuggest = useCallback(async (text, opts) => {
    if (!notionTags.length || text.length < 10) return;
    setSuggesting(true);
    try {
      const res = await suggestNotion(text, opts);
      if (res.data.notion_id) {
        onUpdateQuestion(qIdx, 'notion_id', res.data.notion_id);
      }
    } catch {}
    setSuggesting(false);
  }, [notionTags.length, qIdx, onUpdateQuestion]);

  const trySuggest = useCallback((text) => {
    if (q.notion_id || !notionTags.length || text.length < 10) return;
    if (text === lastSuggested.current) return;
    if (suggestTimer.current) clearTimeout(suggestTimer.current);
    suggestTimer.current = setTimeout(() => {
      lastSuggested.current = text;
      doSuggest(text, q.options);
    }, 1200);
  }, [q.notion_id, q.options, notionTags.length, doSuggest]);

  const handleTextChange = (v) => {
    onUpdateQuestion(qIdx, 'texte', v);
    trySuggest(v);
  };

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: sortableId });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1, zIndex: isDragging ? 50 : 'auto' };

  return (
    <div ref={setNodeRef} style={style} data-testid={`question-card-${qIdx}`}>
      <Card className={`bg-card border-border ${isDragging ? 'ring-2 ring-primary' : ''}`}>
        <CardContent className="p-4 sm:p-5">
          <div className="flex items-start gap-2 sm:gap-3">
            <button type="button" {...attributes} {...listeners} className="flex items-center gap-1 pt-2 text-muted-foreground cursor-grab active:cursor-grabbing touch-none" data-testid={`drag-handle-${qIdx}`}>
              <GripVertical className="w-4 h-4" />
              <span className="text-sm font-bold">Q{qIdx + 1}</span>
            </button>
            <div className="flex-1 space-y-3 min-w-0">
              <FormulaInput multiline value={q.texte} onChange={handleTextChange} placeholder="Texte de la question..." className="min-h-[60px] flex-1" testId={`question-text-${qIdx}`} showPreview={sciMode} sciMode={sciMode} />

              <div className="flex items-center gap-3 flex-wrap">
                <Switch checked={isMulti} onCheckedChange={() => onToggleType(qIdx)} data-testid={`toggle-type-${qIdx}`} className="data-[state=checked]:bg-violet-500" />
                <Label className="text-xs font-medium cursor-pointer select-none" onClick={() => onToggleType(qIdx)}>
                  {isMulti ? 'Réponses multiples' : 'Réponse unique'}
                </Label>
              </div>

              <div className="space-y-2">
                {q.options.map((opt, oIdx) => {
                  const letter = LETTERS[oIdx];
                  const isCorrect = correctSet.has(letter);
                  return (
                    <div key={`${qIdx}-${letter}`} className="flex items-center gap-2">
                      <button type="button" onClick={() => onToggleCorrect(qIdx, letter)}
                        className={`w-8 h-8 flex items-center justify-center text-xs font-bold transition-all shrink-0 ${isMulti ? 'rounded-md' : 'rounded-full'} ${isCorrect ? 'bg-emerald-500 text-white' : 'bg-secondary text-muted-foreground hover:bg-secondary/80'}`}
                        data-testid={`correct-${qIdx}-${letter}`}>{letter}</button>
                      <FormulaInput value={opt} onChange={(v) => onUpdateOption(qIdx, oIdx, v)} placeholder={`Option ${letter}`} className="flex-1" testId={`option-${qIdx}-${oIdx}`} sciMode={sciMode} showPreview={sciMode} />
                      {q.options.length > 2 && <Button variant="ghost" size="sm" onClick={() => onRemoveOption(qIdx, oIdx)} className="text-muted-foreground hover:text-destructive shrink-0"><Trash2 className="w-3 h-3" /></Button>}
                    </div>
                  );
                })}
                {q.options.length < 6 && <Button variant="ghost" size="sm" onClick={() => onAddOption(qIdx)} className="text-xs text-muted-foreground" data-testid={`add-option-${qIdx}`}><Plus className="w-3 h-3 mr-1" /> Ajouter une option</Button>}
              </div>

              {/* Bottom bar: points + notion picker */}
              <div className="flex items-center gap-3 flex-wrap pt-1 border-t border-border/30">
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-muted-foreground">Points:</Label>
                  <Input data-testid={`points-${qIdx}`} type="number" min={0.5} step={0.5} className="w-16 h-7 text-xs" value={q.points} onChange={(e) => onUpdateQuestion(qIdx, 'points', Number(e.target.value))} />
                </div>
                <Label className="text-xs text-muted-foreground">
                  {isMulti ? 'Rép.' : 'Rép.'}: <span className="text-emerald-400 font-bold">{q.bonne_reponse}</span>
                </Label>
                {tagsTree.length > 0 && (
                  <div className="flex items-center gap-1.5 ml-auto min-w-0">
                    <Link to="/tags" title="Gérer les notions" data-testid={`tag-link-${qIdx}`}>
                      <Tag className="w-3.5 h-3.5 text-muted-foreground hover:text-primary cursor-pointer transition-colors shrink-0" />
                    </Link>
                    <NotionPicker tagsTree={tagsTree} value={q.notion_id || ''} onChange={(v) => onUpdateQuestion(qIdx, 'notion_id', v)} testIdPrefix={`np-${qIdx}`} />
                    <Button type="button" variant="ghost" size="sm" className="h-7 px-1.5 text-muted-foreground hover:text-primary" title="Associer la notion par IA"
                      onClick={() => doSuggest(q.texte, q.options)} disabled={suggesting || q.texte.length < 10} data-testid={`ai-suggest-${qIdx}`}>
                      {suggesting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
                    </Button>
                  </div>
                )}
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={() => onRemoveQuestion(qIdx)} className="text-muted-foreground hover:text-destructive shrink-0" data-testid={`remove-question-${qIdx}`} disabled={!canRemove}>
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
